<template>
    <div v-if="config.display" :style="{'width': config.width || '100%'}" class="control e-control">
        <label-wrapper :label-settings="config"></label-wrapper>
        <div v-if="asField">
            <display-value :id="config.id" :value="value"></display-value>
        </div>
        <div v-else>
            <div class="color-controls-container">
                <div class="color-box current-color" title="Click to change color" :class="{ 'no-value': !value }">
                    <div><p>Selected Color:</p></div>
                    <div v-if="!value" class="no-value-box" @click="setDefaultValue()"></div>
                    <div v-if="value">
                        <ejs-colorpicker :id="config.id + '_color_picker'"
                             ref="colorPicker"
                             v-bind="syncfusionConfig"
                             v-model:value="value"
                             :data-test-id="dataTestId"
                             :disabled="config.disabled"
                             @change="colorChanged('colorPicker')"
                             @open="pickerOpened('colorPicker')"
                             @beforeClose="colorPickerCancel('colorPicker')"
                        ></ejs-colorpicker>
                    </div>
                    <div><p class="hex">{{ value || 'No Color Selected' }}</p></div>
                </div>
                <div style="width: 74%;">
                    <button class="edit-button" style="margin-bottom: 5px;" @click="showColorPicker = false; hidePalettes = false">
                        Palettes
                    </button>
                    <div>
                        <a @click="clearValue()" title="Clear Value" v-if="value && !config.disabled" class="clear-color">clear</a>
                    </div>
                    <!-- Color Squares in a Palette -->
                    <div class="edit-palette-container" v-if="!hidePalettes">
                        <div class="palette-backdrop" @click="hidePaletteContainer"></div>
                        <div class="palette-content">
                            <div style="display: flex;">
                                <div style="flex-grow: 1;" v-if="selectedPalette.id === 'newPalette'">
                                    <p style="font-size: 20px; margin: 0; line-height: 33px;">Name your new palette and add colors</p>
                                </div>
                                <div style="flex-grow: 1;" v-show="selectedPalette.id !== 'newPalette'">
                                    <control :name="'PALETTES__' + config.id" :value="selectedOption" @update:value="selectedOption = $event" :prop-config="controlConfigs.palettes" placeholder="Choose a Palette"></control>
                                    <button type="button" class="edit-button close" v-show="showEditPaletteButton" @click="toggleEditPalette">Edit Palette</button>
                                </div>
                                <div style="text-align: right;">
                                    <button class="edit-button close" @click="clearSelectedPalette">{{cancelButtonText}}</button>
                                    <button class="edit-button" v-if="showSaveButton" :disabled="saveDisabled" @click="savePalette()" type="button">Save</button>
                                </div>
                            </div>
                            <div>
                                <div v-if="editPaletteEnabled">
                                    <div>
                                        <label for="color-palette-title">Palette Name</label>
                                        <input id="color-palette-title" v-model="selectedPalette.title" placeholder="Name Your Palette" class="e-input e-outline" type="text" autofocus />
                                    </div>
                                </div>
                                <div style="display: flex">
                                    <div class="palette-div">
                                        <div type="button" v-if="!addingNewColor && editPaletteEnabled" class="color-box new-color-button" style="height: 91px;" :class="{ 'selected-color': addingNewColor }" @click="addNewColor()">
                                            + New Color
                                        </div>
                                        <div type="button" v-if="addingNewColor && editPaletteEnabled" class="color-box" style="height: 91px;" :class="{ 'selected-color': addingNewColor }">
                                            <label for="color-name"><input id="color-name" v-model="selectedColor.label" placeholder="Color Name" class="color-name-input" type="text" /></label>
                                            <ejs-colorpicker :id="config.id + '_edit_new_color_color_picker'"
                                                 ref="editNewColorPicker"
                                                 v-model:value="selectedColor.color"
                                                 @change="colorChanged('editNewColorPicker')"
                                                 @open="pickerOpened('editNewColorPicker')"
                                                 @beforeClose="colorPickerCancel('editNewColorPicker')"
                                            ></ejs-colorpicker>
                                        </div>
                                        <div v-for="(color, index) in selectedPalette.colors" :key="index" class="color-box" :class="{ 'selected-color': selectedColorIndex === index }">
                                            <div v-if="editPaletteEnabled && selectedColorIndex === index">
                                                <label for="color-name"><input id="color-name" v-model="selectedColor.label" placeholder="Color Name" class="color-name-input" type="text" /></label>
                                                <div class="color-square" v-if="editPaletteEnabled && selectedColorIndex === index">
                                                    <ejs-colorpicker :id="config.id + '_edit_color_color_picker'"
                                                         ref="editColorPicker"
                                                         v-model:value="selectedColor.color"
                                                         @change="colorChanged('editColorPicker')"
                                                         @open="pickerOpened('editColorPicker')"
                                                         @beforeClose="colorPickerCancel('editColorPicker')"
                                                    ></ejs-colorpicker>
                                                </div>
                                                <div style="display: flex;">
                                                    <p class="hex" @click="selectColor(index)">{{ color.color }}</p>
                                                    <div class="color-edit-controls" v-if="selectedPalette !== 'newPalette'">
                                                        <button type="button" @click="deleteColor(index)"><span class="e-icons close"></span></button>
                                                    </div>
                                                </div>
                                            </div>
                                            <div v-if="!editPaletteEnabled || selectedColorIndex !== index">
                                                <div>
                                                    <p class="color-title" :title="color.label || color.color" @click="selectColor(index)">{{ color.label || color.color }}</p>
                                                </div>
                                                <div class="color-square" :style="{background:color.color}" @click="selectColor(index)"></div>
                                                <div>
                                                    <p class="hex" @click="selectColor(index)">{{ color.color }}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <button class="edit-button delete" @click="deletePalette(selectedPalette)" v-show="showDeleteButton" type="button">Delete</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <p v-if="config.message" class="input-info" v-html="ddStoreFilter(config.message)"></p>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { defineAsyncComponent } from "@common/defineAsyncComponent";
import { actionStore } from "@common/actionStore";
import { library } from "@common/library/library";
import { controlMixins } from "./controlMixins.js";

const controlComponent = defineAsyncComponent(() => import('@common/control/control.vue'));

let editorSettings = {
    onchange: {
        type: 'actionfield',
        label: 'Change Action',
        itemsSource: 'actionStore.getActionOptions()',
        fields: {
            text: 'text',
            value: 'value'
        },
        default: {
            action: '',
            params: {}
        }
    }
};

export const colorComponent = {
    editorSettings: editorSettings,
    mixins: [controlMixins],
    data() {
        return {
            selectedOption: [],
            controlConfigs: {
                palettes: {
                    id: 'palettes',
                    label: 'Palettes',
                    hideLabel: true,
                    type: 'dropdowntree',
                    itemsSource: 'configStore.getPaletteOptions()',
                    helpText: 'Choose your palette.',
                    default: 'Samaritan Palette',
                    fields: {
                        text: 'title',
                        value: 'id',
                        hasChildren: 'hasChild',
                        parentValue: 'pId',
                    },
                    showClearButton: false,
                }
            },
            addingNewColor: false,
            hidePalettes: true,
            showColorPicker: false,
            showEditPalette: false,
            selectedColorIndex: false,
            newPalette: {
                id: 'newPalette',
                title: '',
                colors: [],
                standard: false
            },
            selectedColor: false,
            openedPickers: {}
        };
    },
    computed: {
        ...mapGetters({
            paletteOptions: 'configStore/paletteOptions'
        }),
        selectedPalette() {
            let palette = this.paletteOptions.find((iPalette) => iPalette.id === this.selectedOption[0]) || false;
            if (palette && palette.id === 'newPalette') palette = this.newPalette;
            return palette;
        },
        showEditPaletteButton() {
            return this.selectedPalette
                && !this.selectedPalette.standard
                && this.selectedPalette.id !== 'newPalette';
        },
        editPaletteEnabled() {
            return this.selectedPalette
                && !this.selectedPalette.standard
                && (this.showEditPalette || this.selectedPalette.id === 'newPalette');
        },
        showDeleteButton() {
            return this.editPaletteEnabled && this.selectedPalette.id !== 'newPalette';
        },
        cancelButtonText() {
            if (this.editPaletteEnabled || this.selectedPalette.id === 'newPalette') return 'Cancel';
            return 'Close';
        },
        showSaveButton() {
            return this.editPaletteEnabled || this.selectedPalette.id === 'newPalette';
        },
        saveDisabled() {
            return this.selectedPalette.standard || !this.selectedPalette.title || this.selectedPalette.colors.length === 0;
        },
    },
    methods: {
        setDefaultValue() {
            this.value = '#eeeeeeff';
            setTimeout(() => {
                this.colorChanged('colorPicker');
            }, 50); // NOSONAR
        },
        clearValue() {
            if (this.config.disabled) return;
            this.value = false;
        },
        // eslint-disable-next-line consistent-return
        async savePalette() {
            let palette = library.cloneObj(this.selectedPalette);
            if (!palette.title) return library.showToast('Please enter a Title for your palette');
            // Generate an ID for new palettes
            if (!palette.id || palette.id === 'newPalette') palette.id = 'palette_' + library.getUniqueID();
            let response = await actionStore.addColorPalette(palette);
            if (response.status === 'success') {
                this.selectedOption = []; // Leave this line as it retriggers getPaletteOptions getter
                setTimeout(() => {
                    this.selectedOption = [palette.id];
                }, 500); // NOSONAR
                this.showEditPalette = false;
                this.resetNewPalette();
                return library.showToast('Palette saved successfully.');
            }
            return library.showToast('We were unable to save your Palette. Please retry.');
        },
        addNewColor() {
            let palette = this.selectedPalette;
            this.selectedColor = {
                color: palette.colors.length ? palette.colors[0].color : '#FF0000FF',
                label: ''
            };
            this.addingNewColor = true;
            this.selectedColorIndex = false;
        },
        async deleteColor(index) {
            let confirm = await library.showDialog('Are you sure you want to delete this color?');
            if (confirm) {
                this.selectedPalette.colors.splice(index, 1);
                let newIndex = this.selectedColorIndex - 1;
                if (newIndex < 0) newIndex = 0;
                this.selectedColorIndex = newIndex;
            }
        },
        async deletePalette(selected) {
            let confirm = await library.showDialog('Are you sure you want to delete this color palette?');
            if (confirm) {
                let response = await actionStore.deleteColorPalette(selected);
                if (response.status === 'success') {
                    this.selectedOption = [];
                    this.showEditPalette = false;
                    this.resetNewPalette();
                    library.showToast('Palette deleted successfully.');
                }
                else library.showToast('We were unable to delete your palette. Please retry.');
            }
        },
        clearSelectedPalette() {
            if (this.selectedOption[0] === 'newPalette') {
                this.selectedOption = [];
            }
            else {
                this.hidePaletteContainer();
                this.resetNewPalette();
            }
        },
        resetNewPalette() {
            this.newPalette.id = 'newPalette';
            this.newPalette.title = '';
            this.newPalette.colors = [];
        },
        selectColor(index) {
            let color = this.selectedPalette.colors[index];
            this.selectedColorIndex = index;
            if (this.editPaletteEnabled) {
                this.selectedColor = color;
            }
            else this.value = color.color;
            this.addingNewColor = false;
        },
        toggleEditPalette() {
            this.showEditPalette = !this.showEditPalette;
            if (this.showEditPalette) this.selectColor(0);
        },
        hidePaletteContainer() {
            this.hidePalettes = true;
            // if (this.$refs.editColorPicker) {
            //     this.$refs.editColorPicker.ej2Instances.tooltipEle.remove();
            // }
        },
        colorChanged(ref) {
            if (this.addingNewColor && this.openedPickers[ref]) {
                this.selectedPalette.colors.unshift(library.cloneObj(this.selectedColor));
                this.selectedColor = this.selectedPalette.colors[0];
                this.selectedColorIndex = 0;
                this.addingNewColor = false;
            }
            else {
                if (!this.openedPickers[ref]) {
                    if (Array.isArray(this.$refs[ref])) this.$refs[ref][0].toggle();
                    else this.$refs[ref].toggle();
                }
                else this.openedPickers[ref] = false;
            }
        },
        pickerOpened(ref) {
            this.openedPickers[ref] = true;
        },
        colorPickerCancel(ref) {
            this.openedPickers[ref] = false;
        }
    },
    components: {
        'control': controlComponent,
        "ejs-colorpicker": defineAsyncComponent(() => import('@common/syncfusion/colorPicker')),
        "ejs-dropdowntree": defineAsyncComponent(() => import('@common/syncfusion/dropdownTree'))
    }
};

export default colorComponent;
</script>

<style scoped>
.e-outline {
    border: 1px solid rgba(0, 0, 0, 0.24);
    border-radius: 3px;
}
.clear-color {
    cursor: pointer;
}
.value-options-container {
    display: inline-block;
    margin-left: 10px;
}
div.color-controls-container {
    display: flex;
    margin-bottom: 5px;
}
.new-color-button {
    height: 91px;
    font-size: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    border:2px solid black;
    cursor: pointer;
}
.edit-palette-container {
    margin-left: 5px;
    position: relative;
    height: 1px;
    z-index: 2;
    left: -18%;
    width: 118%;
}
.edit-palette-container .palette-content {
    overflow-y: auto;
    background: #fff;
    border: 1px solid #eee;
    border-radius: 3px;
    z-index: 3;
    position: relative;
    box-shadow: 0 0 8px 2px rgb(0 0 0 / 25%);
    padding: 5px;
}
.edit-palette-container :deep(.e-input-group.e-control-wrapper) {
    margin-bottom: 0;
}
.palette-backdrop, .color-picker-backdrop {
    height: 100%;
    width: 100%;
    position: fixed;
    top: 0;
    left: 0;
    z-index: 2;
    background-color: rgba(0, 0, 0, 0.3);
}
.color-picker-action-area {
    text-align: right;
    width: 270px;
    z-index: 3;
    position: relative;
}
.close-picker-btn {
    padding: 4px 4px 3px 4px;
    border-radius: 3px 3px 0 0;
    background: white;
    border-left: 1px solid #ddd;
    border-top: 1px solid #ddd;
    border-right: 1px solid #ddd;
}
.color-picker {
    position: relative;
    z-index: 3;
}
.palette-div {
    display: flex;
    justify-content: flex-start;
    flex-wrap: wrap;
    flex-grow: 1;
    position: relative;
    align-content: flex-start
}
.color-box {
    border: 1px solid #c2c2c2;
    width: 24%;
    margin: 0.5%;
    height: fit-content;
    cursor: pointer;
}
.color-box.selected-color {
    border: 2px solid #0f658d;
    box-shadow: 1px 1px 8px rgb(0 0 0 / 40%);
}
.half-palette .color-box {
    width: 48%;
}
.new-palette-container .color-box {
    width: 48%;
}
.color-box.current-color {
    margin: 0 10px 0 0;
}
.edit-button {
    border: 1px solid #c2c2c2;
    padding: 0.92rem;
    height: fit-content;
    display: inline-block;
    border-radius: 3px;
}
.edit-button:hover {
    background: #ececec;
    border: 1px solid #9c9c9c;
}
.edit-button.close {
    border: 0 none;
    text-decoration: underline;
}
.edit-button[disabled] {
    cursor: not-allowed;
}
.color-box p {
    color: #404040;
    font-size: 1rem;
    font-weight: 500;
    margin: 0;
    padding: 3px 5px;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
}
.color-box.no-value .no-value-box {
    height: 40px;
    border-top: 1px solid #ccc;
    border-bottom: 1px solid #ccc;
    background: linear-gradient(16deg, rgba(255,255,255,1) 0%, rgba(255,255,255,1) 48%, rgba(189,3,3,1) 49%, rgba(189,3,3,1) 52%, rgba(255,255,255,1) 53%, rgba(255,255,255,1) 100%) !important;
}
.color-box > div .e-icons {
    opacity: 0.25;
}
.color-box > div .e-icons:hover {
    opacity: 1;
}
.e-icons.close:before {
    content: '\e7fc' !important;
}
.color-box.show {
    display: flex;
}
.color-box.hide {
    display: none;
}

.color-square {
    height: 40px;
    width: 100%;
    cursor: pointer;
}
.color-picker-container {
    z-index: 2;
    position: relative;
    height: 1px;
    width: 1px;
    overflow: visible;
}
.color-edit-controls {
    display: none;
}
.color-edit-controls button {
    padding: 6px 4px 2px 4px;
}
.hex {
    flex-grow: 1;
}
.color-box:hover .color-edit-controls {
    display: inline-block;
}
.button-container {
    display:flex;
    flex-grow: 1;
    justify-content: flex-end;
}
.button-container button {
    margin: 0 5px;
}
.color-box :deep(.e-split-colorpicker.e-control) {
    padding: 0;
    width: 100%;
}
.color-box :deep(.e-colorpicker-wrapper), .color-box :deep(.e-colorpicker-wrapper .e-split-btn-wrapper) {
    width: 100%;
}
.color-box :deep(.e-colorpicker-wrapper .e-split-btn-wrapper .e-btn-icon.e-selected-color) {
    width: 100%;
    height: 40px;
    border-radius: 0;
}
.color-box :deep(.e-colorpicker-wrapper .e-split-btn-wrapper .e-btn-icon.e-selected-color .e-split-preview) {
    border-radius: 0;
}
.color-box :deep(.e-split-btn-wrapper .e-dropdown-btn) {
    display: none;
}
.color-name-input {
    width: 100%;
    padding: 3px 2px;
    border: 0 none;
    border-bottom: 1px solid rgba(0, 0, 0, 0.24);
}
</style>
